import { DynamicFormV2Palette, Theme } from "@mui/material";
import { StripeElementsOptions } from "@stripe/stripe-js";

export type FormPaymentMethodComponentTokenType = {
  textColor: string;
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
  radioColor: string;
  institutionOptionBackgroundColor: string;
  institutionOptionTextColor: string;
  institutionOptionBorderColor: string;
  institutionOptionHoverBorderColor: string;
  institutionOptionHoverBackgroundColor: string;
  institutionOptionSelectedBorderColor: string;
  institutionOptionSelectedBackgroundColor: string;
  institutionOptionSelectedTextColor: string;
  chequeCustomMessageBackgroundColor: string;
  chequeCustomMessageTextColor: string;
  chequeCustomMessageBorderColor: string;
};

export const assignPaymentMethodComponentTokens = (
  formsPalette: DynamicFormV2Palette["forms"],
  mode: "light" | "dark"
) => {
  const isLightMode = mode === "light";

  return {
    textColor: formsPalette.text.moderate,
    iconColor: formsPalette.text.moderate,
    radioColor: formsPalette.text.moderate,
    backgroundColor: isLightMode ? formsPalette.surface.supershy : formsPalette.surface.intense,
    borderColor: isLightMode ? formsPalette.border.moderate : formsPalette.border.supershy,
    chequeCustomMessageBackgroundColor: formsPalette.surface.quiet,
    chequeCustomMessageTextColor: formsPalette.text.moderate,
    chequeCustomMessageBorderColor: isLightMode ? formsPalette.border.moderate : formsPalette.border.supershy,
    institutionOptionBackgroundColor: isLightMode ? formsPalette.surface.reverse : formsPalette.surface.quiet,
    institutionOptionTextColor: formsPalette.text.moderate,
    institutionOptionBorderColor: isLightMode ? formsPalette.border.moderate : formsPalette.border.supershy,
    institutionOptionHoverBorderColor: formsPalette.border.brand,
    institutionOptionHoverBackgroundColor: formsPalette.surface.hover.quiet,
    institutionOptionSelectedBorderColor: isLightMode ? formsPalette.surface.moderate : formsPalette.surface.reverse, // We made an exception here, we're using a surface token for a border
    institutionOptionSelectedBackgroundColor: isLightMode
      ? formsPalette.surface.moderate
      : formsPalette.surface.reverse,
    institutionOptionSelectedTextColor: isLightMode ? formsPalette.text.moderate : formsPalette.text.reverse,
  } as FormPaymentMethodComponentTokenType;
};

type GetStripeAppearanceOptions = {
  hasMoreItemsAbove?: boolean;
  hasMoreItemsBelow?: boolean;
};

const defaultOptions: GetStripeAppearanceOptions = {
  hasMoreItemsAbove: false,
  hasMoreItemsBelow: false,
};

export const getStripeAppearance = (
  theme: Theme,
  options: GetStripeAppearanceOptions = {}
): StripeElementsOptions["appearance"] => {
  const o = { ...defaultOptions, ...options };

  const tokens = theme.palette.formsComponents.paymentMethod;
  const textFieldTokens = theme.palette.formsComponents.textField;

  // https://docs.stripe.com/elements/appearance-api
  return {
    rules: {
      ".Label": theme.typography.body2 as Record<string, string>,
      ".AccordionItem": {
        borderColor: tokens.borderColor,
        ...(o.hasMoreItemsAbove && { borderTopLeftRadius: "0px", borderTopRightRadius: "0px" }),
        ...(o.hasMoreItemsBelow && { borderBottomLeftRadius: "0px", borderBottomRightRadius: "0px" }),
        boxShadow: "none",
      },
      ".Input": {
        ...(theme.typography.body2 as Record<string, string>),
        padding: theme.spacing(1),
        borderRadius: theme.radius(1),
        boxShadow: "none",
        color: textFieldTokens.textColor,
        fill: "currentColor",
        border: `1px solid ${textFieldTokens.borderColor}`,
        backgroundColor: textFieldTokens.backgroundColor,
        outline: "0px solid transparent",
        transition: "outline 100ms ease-out, border-color 200ms ease-out",
      },
      ".Input:hover": {
        borderColor: textFieldTokens.hoverBorderColor,
      },
      ".Input:focus": {
        outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
      },
      ".Input:disabled": {
        opacity: "0.4px",
      },
      ".Error": {
        ...(theme.typography.caption as Record<string, string>),
      },
      ".PickerItem": {
        padding: theme.spacing(1.5),
        borderRadius: theme.radius(1),
        border: `1px solid ${tokens.institutionOptionBorderColor}`,
        backgroundColor: tokens.institutionOptionBackgroundColor,
        color: tokens.institutionOptionTextColor,
        transition:
          "outline 100ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out, color 200ms ease-out",
        boxShadow: "none",
      },
      ".PickerItem:hover": {
        backgroundColor: tokens.institutionOptionHoverBackgroundColor,
        borderColor: tokens.institutionOptionHoverBorderColor,
        color: tokens.institutionOptionTextColor,
        boxShadow: "none",
      },
      ".PickerItem--selected": {
        backgroundColor: tokens.institutionOptionSelectedBackgroundColor,
        borderColor: tokens.institutionOptionSelectedBorderColor,
        color: tokens.institutionOptionSelectedTextColor,
        cursor: "pointer",
      },
      ".PickerItem:active": {
        backgroundColor: tokens.institutionOptionSelectedBackgroundColor,
        borderColor: tokens.institutionOptionSelectedBorderColor,
        color: tokens.institutionOptionSelectedTextColor,
      },
    },
    variables: {
      colorPrimary: tokens.textColor,
      colorText: tokens.textColor,
      colorTextSecondary: tokens.textColor,
      iconColor: tokens.iconColor,
      colorBackground: tokens.backgroundColor,
      fontFamily: theme.typography.body2.fontFamily,
      borderRadius: theme.radius(1),
      colorDanger: textFieldTokens.errorBorderColor,
      colorTextPlaceholder: textFieldTokens.placeholderTextColor,
      focusBoxShadow: "none",
    },
  };
};
